/**
 * API client for Keyframes
 */

import { useAuthStore } from '@/stores/auth'

// Use VITE_API_URL for deployed environments, relative path for local dev
const API_BASE = (import.meta.env.VITE_API_URL || '') + '/api/v1'

interface ApiOptions {
    method?: string
    body?: unknown
    headers?: Record<string, string>
}

interface ApiResponse<T = unknown> {
    success: boolean
    data?: T
    error?: {
        code: string
        message: string
        details?: Record<string, string[]>
    }
    pagination?: {
        page: number
        limit: number
        total: number
        totalPages: number
        hasMore: boolean
    }
}

class ApiError extends Error {
    code: string
    details?: Record<string, string[]>
    status: number

    constructor(message: string, code: string, status: number, details?: Record<string, string[]>) {
        super(message)
        this.name = 'ApiError'
        this.code = code
        this.status = status
        this.details = details
    }
}

async function request<T>(endpoint: string, options: ApiOptions = {}): Promise<ApiResponse<T>> {
    const { method = 'GET', body, headers = {} } = options

    const authStore = useAuthStore.getState()
    const accessToken = authStore.accessToken

    const requestHeaders: Record<string, string> = {
        'Content-Type': 'application/json',
        ...headers,
    }

    if (accessToken) {
        requestHeaders['Authorization'] = `Bearer ${accessToken}`
    }

    const response = await fetch(`${API_BASE}${endpoint}`, {
        method,
        headers: requestHeaders,
        body: body ? JSON.stringify(body) : undefined,
    })

    const data = await response.json() as ApiResponse<T>

    if (!response.ok || !data.success) {
        // Handle token expiration
        if (response.status === 401 && data.error?.code === 'TOKEN_EXPIRED') {
            const refreshed = await authStore.refreshTokens()
            if (refreshed) {
                // Retry the request with new token
                return request(endpoint, options)
            }
        }

        throw new ApiError(
            data.error?.message || 'An error occurred',
            data.error?.code || 'UNKNOWN_ERROR',
            response.status,
            data.error?.details
        )
    }

    return data
}

export const api = {
    get: <T>(endpoint: string, headers?: Record<string, string>) =>
        request<T>(endpoint, { method: 'GET', headers }),

    post: <T>(endpoint: string, body?: unknown, headers?: Record<string, string>) =>
        request<T>(endpoint, { method: 'POST', body, headers }),

    put: <T>(endpoint: string, body?: unknown, headers?: Record<string, string>) =>
        request<T>(endpoint, { method: 'PUT', body, headers }),

    patch: <T>(endpoint: string, body?: unknown, headers?: Record<string, string>) =>
        request<T>(endpoint, { method: 'PATCH', body, headers }),

    delete: <T>(endpoint: string, headers?: Record<string, string>) =>
        request<T>(endpoint, { method: 'DELETE', headers }),
}

export { ApiError }
export type { ApiResponse }
